// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { ShieldCheck, Loader2, Check, AlertTriangle } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'

/**
 * Admin panel to configure a pharmacy's Entra ID SSO. Stores tenant/client id + the Key Vault
 * reference for the secret (never the secret itself). The live connection still requires the
 * pharmacy's Entra admins to register the OwlRx app + place the secret in Key Vault.
 */
export default function PharmacySsoConfig({ pharmacyId }: { pharmacyId: string }) {
  const [cfg, setCfg] = useState<api.PharmacySsoConfig | null>(null)
  const [f, setF] = useState({ enabled: false, entra_tenant_id: '', entra_client_id: '', client_secret_kv_ref: '', allowed_email_domains: '', jit_provisioning: true, require_sso: false })
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    api.getPharmacySso(pharmacyId).then(c => {
      setCfg(c)
      setF(s => ({
        ...s, enabled: !!c.enabled, entra_tenant_id: c.entra_tenant_id || '', entra_client_id: c.entra_client_id || '', client_secret_kv_ref: c.client_secret_kv_ref || "",
        allowed_email_domains: c.allowed_email_domains ? JSON.parse(c.allowed_email_domains).join(', ') : '',
        jit_provisioning: c.jit_provisioning ?? true, require_sso: c.require_sso ?? false,
      }))
    }).catch(e => setErr(friendlyError(e)))
  }, [pharmacyId])

  const save = async () => {
    setBusy(true); setMsg(null); setErr(null)
    try {
      const r = await api.putPharmacySso(pharmacyId, {
        enabled: f.enabled,
        entra_tenant_id: f.entra_tenant_id || undefined,
        entra_client_id: f.entra_client_id || undefined,
        client_secret_kv_ref: f.client_secret_kv_ref || undefined,
        allowed_email_domains: f.allowed_email_domains ? f.allowed_email_domains.split(',').map(d => d.trim()).filter(Boolean) : undefined,
        jit_provisioning: f.jit_provisioning, require_sso: f.require_sso,
      })
      setMsg(`Saved. Authority: ${r.authority || '—'}`)
      setCfg(await api.getPharmacySso(pharmacyId))
    } catch (e: any) { setErr(friendlyError(e)) } finally { setBusy(false) }
  }

  const set = (k: string, v: any) => setF(s => ({ ...s, [k]: v }))

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: 18, background: 'var(--color-surface)', marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <ShieldCheck size={18} style={{ color: 'var(--color-primary)' }} />
        <strong style={{ fontSize: 15 }}>Single sign-on — Microsoft Entra ID</strong>
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--color-text-mute)', margin: '0 0 14px' }}>
        Federate this pharmacy's login to their Entra tenant. MFA is enforced by the tenant's
        Conditional Access. The client secret lives in Key Vault — store only its reference name here.
      </p>

      {cfg && !cfg.secret_configured && f.entra_client_id && (
        <div style={{ display: 'flex', gap: 8, fontSize: 12.5, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
          <AlertTriangle size={16} /> Client secret not detected in Key Vault under that reference. SSO login will return “not configured” until it's present.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <L label="Entra tenant ID"><input style={inp} value={f.entra_tenant_id} onChange={e => set('entra_tenant_id', e.target.value)} placeholder="xxxxxxxx-xxxx-…" /></L>
        <L label="Application (client) ID"><input style={inp} value={f.entra_client_id} onChange={e => set('entra_client_id', e.target.value)} placeholder="app registration client id" /></L>
        <L label="Client secret — Key Vault reference name"><input style={inp} value={f.client_secret_kv_ref} onChange={e => set('client_secret_kv_ref', e.target.value)} placeholder="e.g. SSO_ENTRA_APPLE_SECRET" /></L>
        <L label="Allowed email domains (comma-separated)"><input style={inp} value={f.allowed_email_domains} onChange={e => set('allowed_email_domains', e.target.value)} placeholder="apple.com" /></L>
      </div>
      <div style={{ display: 'flex', gap: 18, marginTop: 12, flexWrap: 'wrap' }}>
        <Toggle label="Enable SSO" checked={f.enabled} onChange={v => set('enabled', v)} />
        <Toggle label="Auto-provision accounts (JIT)" checked={f.jit_provisioning} onChange={v => set('jit_provisioning', v)} />
        <Toggle label="Require SSO (disable password login)" checked={f.require_sso} onChange={v => set('require_sso', v)} />
      </div>
      <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--color-text-mute)' }}>
        Redirect URIs to register in Entra (register both during the owlrx migration):
        <br /><code>https://owlrx.owlconsultations.com/api/auth/sso/callback</code>
        <br /><code style={{ opacity: 0.7 }}>https://owlrxmu.owlconsultations.com/api/auth/sso/callback</code>
      </div>

      {msg && <div style={{ color: 'var(--color-primary)', fontSize: 12.5, marginTop: 10, display: 'flex', gap: 6, alignItems: 'center' }}><Check size={14} /> {msg}</div>}
      {err && <div style={{ color: '#dc2626', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
      <div style={{ marginTop: 14 }}>
        <button style={primary} disabled={busy} onClick={save}>{busy ? <Loader2 className="spin" size={14} /> : <ShieldCheck size={14} />} Save SSO configuration</button>
      </div>
    </div>
  )
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'block', fontSize: 12 }}><span style={{ color: 'var(--color-text-mute)', display: 'block', marginBottom: 4 }}>{label}</span>{children}</label>
}
function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ width: 16, height: 16 }} /> {label}
    </label>
  )
}
const inp: React.CSSProperties = { width: '100%', padding: '7px 9px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-surface)', color: 'var(--color-text)', boxSizing: 'border-box' }
const primary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
